import { storage } from './indexedDB';

const STORES = {
  IMAGES: 'images',
  IMAGE_INDEX: 'imageIndex'
};

const KEYS = {
  SERVER_IMAGES: 'hypwr_server_images',
  IMAGE_INDEX: 'hypwr_image_index'
};

/**
 * 图片索引服务 - 与服务器同步图片元数据
 */
export const imageIndexService = {
  /**
   * 从服务器获取图片索引
   */
  async syncFromServer(): Promise<{ success: boolean; count: number }> {
    try {
      const response = await fetch('/api/images/sync');
      const data = await response.json();
      
      if (data.success) {
        // 保存索引信息
        await storage.setItem(KEYS.IMAGE_INDEX, STORES.IMAGE_INDEX, {
          lastSync: new Date().toISOString(),
          categories: data.categories,
          totalCount: data.count
        });
        
        return { success: true, count: data.count };
      }
      
      return { success: false, count: 0 };
    } catch (error) {
      console.error('Sync images failed:', error);
      return { success: false, count: 0 };
    }
  },

  /**
   * 获取服务器上的图片列表
   */
  async getServerImages(category?: string): Promise<any[]> {
    try {
      const url = category 
        ? `/api/images?category=${encodeURIComponent(category)}`
        : '/api/images';
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.images) {
        // 保存到本地缓存
        const key = category ? `${KEYS.SERVER_IMAGES}_${category}` : KEYS.SERVER_IMAGES;
        await storage.setItem(key, STORES.IMAGES, data.images);
        return data.images;
      }
      
      return [];
    } catch (error) {
      console.error('Get server images failed:', error);
      // 尝试返回缓存数据
      const key = category ? `${KEYS.SERVER_IMAGES}_${category}` : KEYS.SERVER_IMAGES;
      const cached = await storage.getItem<any[]>(key, STORES.IMAGES);
      return cached || [];
    }
  },

  /**
   * 获取分类下的所有图片路径
   */
  async getImagesByCategory(category: string): Promise<string[]> {
    const images = await this.getServerImages(category);
    return images.map((img: any) => img.path);
  },

  /**
   * 自动发现服务器上的图片并同步到本地配置
   * 用于初始化页面内容时自动填充图片
   */
  async autoDiscoverImages(): Promise<{
    logo?: string;
    hero?: string;
    about?: string;
    qrCode?: string;
    news: string[];
    products: string[];
    applications: string[];
  }> {
    const result: any = {
      news: [],
      products: [],
      applications: []
    };

    // 获取各类图片
    const logoImages = await this.getServerImages('logo');
    if (logoImages.length > 0) {
      result.logo = logoImages[0].path;
    }

    const heroImages = await this.getServerImages('hero');
    if (heroImages.length > 0) {
      result.hero = heroImages[0].path;
    }

    const aboutImages = await this.getServerImages('about');
    if (aboutImages.length > 0) {
      result.about = aboutImages[0].path;
    }

    const qrImages = await this.getServerImages('qrcode');
    if (qrImages.length > 0) {
      result.qrCode = qrImages[0].path;
    }

    const newsImages = await this.getServerImages('news');
    result.news = newsImages.map((img: any) => img.path);

    const productImages = await this.getServerImages('products');
    result.products = productImages.map((img: any) => img.path);

    const appImages = await this.getServerImages('applications');
    result.applications = appImages.map((img: any) => img.path);

    return result;
  },

  /**
   * 上传图片，自动去重
   */
  async uploadImage(file: File, category?: string, metadata?: any): Promise<{
    success: boolean;
    url?: string;
    deduplicated?: boolean;
    message?: string;
  }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (category) {
        formData.append('category', category);
      }
      if (metadata?.tags) {
        formData.append('tags', metadata.tags.join(','));
      }
      if (metadata?.description) {
        formData.append('description', metadata.description);
      }

      const response = await fetch('/upload', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        // 刷新对应分类的缓存
        if (category) {
          await this.getServerImages(category);
        }
        
        return {
          success: true,
          url: data.url,
          deduplicated: data.deduplicated,
          message: data.message
        };
      }

      return { success: false, message: data.error };
    } catch (error) {
      console.error('Upload failed:', error);
      return { success: false, message: 'Upload failed' };
    }
  },

  /**
   * 检查图片是否已存在（通过哈希）
   */
  async checkDuplicate(file: File): Promise<{
    exists: boolean;
    url?: string;
  }> {
    // 这里需要在客户端计算文件哈希
    // 简化处理：先上传，服务器会返回是否重复
    return { exists: false };
  },

  /**
   * 获取最后一次同步时间
   */
  async getLastSyncTime(): Promise<string | null> {
    const index = await storage.getItem<any>(KEYS.IMAGE_INDEX, STORES.IMAGE_INDEX);
    return index?.lastSync || null;
  }
};

export default imageIndexService;
